import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { getDeals } from "api/requests/getDeals";
import { Action, Category } from "constants/events";
import { Deal } from "custom-types/Deals";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useGetDealKindLabel from "hooks/useGetDealKindLabel";
import { getCoordinates } from "redux/selectors/location";
import { getUserId } from "redux/selectors/user";
import { dealCardUpdateMedRec } from "utils/dealCardUpdateMedRec";
import { getDispensaryUrl } from "utils/dispensaryUrls";
import { getRoundedDistance } from "utils/distance";

import DealCard from "components/botanic//DealCard";
import Button from "components/botanic/Button";

type Props = {
  className?: string;
  dealsProp: Deal[];
};

const DEALS_PER_CLICK = 6;

const DealsResults: React.FC<Props> = ({ className, dealsProp }) => {
  const countryCode = useDomainCountryCode();
  const isCanada = countryCode === "CA";
  const coordinates = useSelector(getCoordinates);
  const userId = useSelector(getUserId);
  const dispatch = useDispatch();

  const [deals, setDeals] = useState<Deal[]>(dealsProp || []);
  const [visibleDealsCount, setVisibleDealsCount] = useState(DEALS_PER_CLICK);
  const { publishEvent } = useEventTracker();
  const { getDealKindLabel } = useGetDealKindLabel();

  const showLoadMore = deals.length > visibleDealsCount;

  useEffect(() => {
    setDeals(dealsProp);
  }, [dealsProp]);

  const handleLoadMoreClick = async () => {
    if (!showLoadMore) {
      return;
    }
    publishEvent({
      action: Action.click,
      category: Category.loadMoreDealsButton,
      label: "",
    });
    const { deals: newDeals } = await getDeals(
      coordinates,
      countryCode,
      deals?.length,
      DEALS_PER_CLICK
    );

    setDeals(deals.concat(newDeals));
    setVisibleDealsCount(visibleDealsCount + DEALS_PER_CLICK);
  };

  const cardClick = async (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId, distanceMi },
      id: dealId,
    } = deal;

    publishEvent({
      action: Action.click,
      category: Category.dealsMenuBrowseDeals,
      dealId,
      dispensaryDistance: getRoundedDistance(distanceMi, countryCode),
      dispensaryId: dispensaryId,
      label: getDealKindLabel(deal),
      userId: userId as number,
    });

    // Med/rec deals will have medical and/or recreational properties, check if we need to update the user preference
    if (deal.isMedical || deal.isRecreational) {
      await dealCardUpdateMedRec(deal, dispatch);
    }
  };

  const cardImpression = (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId, distanceMi },
      id: dealId,
    } = deal;

    publishEvent({
      action: Action.impression,
      category: Category.dealsMenuBrowseDeals,
      dealId,
      dispensaryDistance: getRoundedDistance(distanceMi, countryCode),
      dispensaryId: dispensaryId,
      label: getDealKindLabel(deal),
      nonInteractionEvent: true,
      userId: userId as number,
    });
  };

  return (
    <div className={classnames(className)}>
      <h2 className="heading--l mb-lg">
        {isCanada ? "Best 420 Deals Near You" : "Find 420 Deals Near You"}
      </h2>
      <div className="row -mb-md">
        {deals.slice(0, visibleDealsCount).map((deal: Deal) => {
          const {
            title,
            dispensary: { slug },
          } = deal;

          const isDealCBD = !deal.isMedical && !deal.isRecreational;
          const dealType =
            deal.isMedical && !deal.isRecreational ? "Med" : "Rec";

          const dealUrl = getDispensaryUrl({
            countryCode,
            query: {
              deal_title: [title],
              ...(!isDealCBD && { menu_type: dealType }),
            },
            slug,
            tab: "menu",
          });

          return (
            <div key={deal.id} className="col md:col-1/3 mb-md">
              <DealCard
                data-testid="results-deal-card"
                deal={{
                  ...deal,
                  dealUrl,
                }}
                showFulfillment={true}
                countryCode={countryCode}
                className="rounded h-full overflow-hidden elevation-low"
                onClick={() => cardClick(deal)}
                onLoad={() => cardImpression(deal)}
              />
            </div>
          );
        })}
      </div>

      {showLoadMore && (
        <Button
          data-testid="load-more-deals"
          onClick={handleLoadMoreClick}
          className="mt-xxl"
        >
          load more deals
        </Button>
      )}
    </div>
  );
};

export default DealsResults;
